<template>
  <div
    class="select-item"
    :class="{ highlighted: isHighlighted, 'select-item--left-wide': hasSlotLeft && leftWide }"
    data-test-id="select-item"
  >
    <CheckBoxToggle
      :id="id"
      v-if="controlStyle === 'toggle'"
      ref="Input"
      :checked="isSelected"
      :disabled="isDisabled"
      position="end"
      @update:checked="$emit('toggle', $event)"
      @keyup.enter.exact="$emit('toggle', $event)"
      @focusout="stopFocusBubble"
    >
      <slot name="left" />

      <div class="primary-text">
        <span :title="primaryText">{{ primaryText }}</span>
      </div>
    </CheckBoxToggle>
    <RadioButton
      v-else-if="controlStyle === 'radio'"
      ref="Input"
      :model="isSelected"
      :modelValue="true"
      :disabled="isDisabled"
      :id="id"
      @click="$emit('toggle', $event)"
      @keyup.enter.exact="$emit('toggle', $event)"
      @focusout="stopFocusBubble"
    >
      <slot name="left" />

      <slot>{{ primaryText }}</slot>
    </RadioButton>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { pseudoRandomId } from '@utils/pseudoRandomId';
import { hasSlotContent } from '@components/slots';

/**
 * Use this component for a SelectionList with clickableOption="custom-control"
 *
 * It looks like the default controls, but supports icon / extra content slot for both
 * checkbox toggle and radio button
 */
export default defineComponent({
  name: 'SelectItem',
  emits: { toggle: null },

  props: {
    primaryText: {
      type: String,
      required: true,
    },
    /**
     * The item is highlighted, akin to hover, but with keyboard
     */
    isHighlighted: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The item should be focussed
     */
    isFocussed: {
      type: Boolean,
      required: false,
      default: false,
    },

    isSelected: {
      type: Boolean,
      required: true,
    },

    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Set to true if the left slot is 24px wide
     */
    leftWide: {
      type: Boolean,
      default: false,
    },

    controlStyle: {
      type: String as PropType<'toggle' | 'radio'>,
      default: 'toggle',
    },
  },

  data() {
    return {
      id: pseudoRandomId(),
    };
  },

  computed: {
    hasSlotLeft() {
      return hasSlotContent(this.$slots.left, {});
    },
  },

  methods: {
    stopFocusBubble(event: FocusEvent) {
      event.stopPropagation();
    },
  },

  watch: {
    isFocussed(newValue) {
      if (newValue) {
        this.$refs.Input.focus();
      }
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
.select-item {
  width: 100%;
  height: 32px;
  display: flex;
  gap: 8px;
  align-items: center;
  border-radius: 8px;
  padding: 0 4px 0 8px;

  --icon-size: 24px;

  &:hover,
  &.highlighted {
    background-color: colors.$color-grey-100;
  }

  // If the left slot element is 24px wide, the padding must be reduced
  // so the distance is the same as for elements 16px wide
  &--left-wide {
    padding-left: 4px;
  }

  // quick fix for icon sizes, should be touched in the selection list refactoring
  svg {
    flex: 0 0 auto;
  }

  .primary-text {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    line-height: 18px;
  }

  .radio-button__label {
    width: 100%;
  }
}
</style>
